'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Check, Copy } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { config } from '@/lib/config'

// ============================================================================
// Schema
// ============================================================================

const credentialsSchema = z.object({
  secretKey: z.string().min(1, 'Secret key is required'),
  publicKey: z.string().min(1, 'Public key is required'),
  merchantCode: z.string().min(1, 'Merchant code is required'),
  region: z.enum(['ae_kw', 'sa']),
  webhookHeaderTitle: z.string().optional(),
  webhookHeaderValue: z.string().optional(),
})

type CredentialsValues = z.infer<typeof credentialsSchema>

interface TabbyFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
  storeId?: string
}

// ============================================================================
// Component
// ============================================================================

export function TabbyForm({ defaultValues = {}, onSubmit, isLoading = false }: TabbyFormProps) {
  const { toast } = useToast()
  const hasExistingCredentials = !!(defaultValues.credentials?.secretKey)

  const [sandboxed, setSandboxed] = useState<boolean>(defaultValues.credentials?.sandboxed ?? false)
  const [copiedWebhook, setCopiedWebhook] = useState(false)

  const form = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      secretKey: defaultValues.credentials?.secretKey ?? '',
      publicKey: defaultValues.credentials?.publicKey ?? '',
      merchantCode: defaultValues.credentials?.merchantCode ?? '',
      region: defaultValues.credentials?.region ?? 'ae_kw',
      webhookHeaderTitle: defaultValues.credentials?.webhookHeaderTitle ?? '',
      webhookHeaderValue: defaultValues.credentials?.webhookHeaderValue ?? '',
    },
  })

  const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/tabby`

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopiedWebhook(true)
      setTimeout(() => setCopiedWebhook(false), 2000)
      toast({ title: 'Copied', description: 'Webhook URL copied to clipboard' })
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard', variant: 'destructive' })
    }
  }

  const handleFormSubmit = async (data: CredentialsValues) => {
    await onSubmit({
      credentials: {
        ...data,
        sandboxed,
        webhookHeaderTitle: data.webhookHeaderTitle || undefined,
        webhookHeaderValue: data.webhookHeaderValue || undefined,
      },
    })
  }

  return (
    <div className="space-y-6">
      {hasExistingCredentials && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">Tabby Connected</AlertTitle>
          <AlertDescription className="text-green-700">
            Your Tabby integration is active. Customers can use Tabby BNPL at checkout.
          </AlertDescription>
        </Alert>
      )}

      <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* API Credentials */}
        <Card>
          <CardHeader>
            <CardTitle>API Credentials</CardTitle>
            <CardDescription>
              Get your API keys from the{' '}
              <a href="https://merchant.tabby.ai" target="_blank" rel="noopener noreferrer" className="underline">
                Tabby Merchant Dashboard
              </a>{' '}
              (UAE/Kuwait) or{' '}
              <a href="https://merchant.tabby.sa" target="_blank" rel="noopener noreferrer" className="underline">
                merchant.tabby.sa
              </a>{' '}
              (KSA). Contact your Tabby integration manager to obtain your merchant code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="secretKey">
                Secret Key <span className="text-destructive">*</span>
              </Label>
              <Input
                id="secretKey"
                type="password"
                placeholder="sk_..."
                disabled={isLoading}
                {...form.register('secretKey')}
              />
              <p className="text-xs text-muted-foreground">
                Backend-only — used for server-side API calls. Never exposed to the browser.
              </p>
              {form.formState.errors.secretKey && (
                <p className="text-xs text-destructive">{form.formState.errors.secretKey.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="publicKey">
                Public Key <span className="text-destructive">*</span>
              </Label>
              <Input
                id="publicKey"
                placeholder="pk_..."
                disabled={isLoading}
                {...form.register('publicKey')}
              />
              <p className="text-xs text-muted-foreground">
                Safe to use in the browser for the Tabby installment widget.
              </p>
              {form.formState.errors.publicKey && (
                <p className="text-xs text-destructive">{form.formState.errors.publicKey.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="merchantCode">
                Merchant Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="merchantCode"
                placeholder="your_merchant_code"
                disabled={isLoading}
                {...form.register('merchantCode')}
              />
              <p className="text-xs text-muted-foreground">
                Provided by your Tabby integration manager. Required in every API request.
              </p>
              {form.formState.errors.merchantCode && (
                <p className="text-xs text-destructive">{form.formState.errors.merchantCode.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Region */}
        <Card>
          <CardHeader>
            <CardTitle>Region</CardTitle>
            <CardDescription>
              Select the region where your store operates. This determines which Tabby API endpoint is used.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="ae_kw"
                  disabled={isLoading}
                  {...form.register('region')}
                />
                <span className="text-sm font-medium">UAE & Kuwait</span>
                <span className="text-xs text-muted-foreground">(api.tabby.ai)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="sa"
                  disabled={isLoading}
                  {...form.register('region')}
                />
                <span className="text-sm font-medium">Saudi Arabia</span>
                <span className="text-xs text-muted-foreground">(api.tabby.sa)</span>
              </label>
            </div>
            {form.formState.errors.region && (
              <p className="text-xs text-destructive">{form.formState.errors.region.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Sandbox Mode */}
        <Card>
          <CardHeader>
            <CardTitle>Environment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Sandbox / Test Mode</Label>
                <p className="text-xs text-muted-foreground">
                  Use test credentials and test phone numbers. OTP: 8888.
                </p>
              </div>
              <Switch checked={sandboxed} onCheckedChange={setSandboxed} disabled={isLoading} />
            </div>
          </CardContent>
        </Card>

        {/* Webhook Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Webhook Setup</CardTitle>
            <CardDescription>
              Register this URL in your Tabby Merchant Dashboard under Settings → Webhooks,
              or via the{' '}
              <a href="https://docs.tabby.ai/api-reference/webhooks/register-a-webhook" target="_blank" rel="noopener noreferrer" className="underline">
                Tabby Webhooks API
              </a>
              . Tabby uses webhooks to notify you of payment status changes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <div className="flex items-center gap-2">
                <Input value={webhookUrl} readOnly className="font-mono text-sm bg-muted" />
                <Button type="button" variant="outline" size="sm" onClick={handleCopyWebhook}>
                  {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhookHeaderTitle">Signing Header Name (optional)</Label>
              <Input
                id="webhookHeaderTitle"
                placeholder="X-Tabby-Signature"
                disabled={isLoading}
                {...form.register('webhookHeaderTitle')}
              />
              <p className="text-xs text-muted-foreground">
                The custom header name Tabby will include in webhook requests for verification.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhookHeaderValue">Signing Header Value (optional)</Label>
              <Input
                id="webhookHeaderValue"
                type="password"
                placeholder="your-secret-value"
                disabled={isLoading}
                {...form.register('webhookHeaderValue')}
              />
              <p className="text-xs text-muted-foreground">
                The secret value sent in the signing header. Must match what you configure in Tabby.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Setup Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
              <li>Log in to your Tabby Merchant Dashboard and navigate to <strong>Settings → API Keys</strong>.</li>
              <li>Copy your <strong>Secret Key</strong> and <strong>Public Key</strong> and paste them above.</li>
              <li>Contact your Tabby integration manager to get your <strong>Merchant Code</strong>.</li>
              <li>Select the region matching your store (UAE/Kuwait or KSA).</li>
              <li>Copy the Webhook URL above and register it in Tabby&apos;s webhook settings. Optionally set a signing header.</li>
              <li>Enable sandbox mode for testing using the test credentials from{' '}
                <a href="https://docs.tabby.ai/testing-guidelines/testing-credentials" target="_blank" rel="noopener noreferrer" className="underline">
                  Tabby Testing Guidelines
                </a>.
              </li>
            </ol>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
